/**
 * The update channel (DEV-1021).
 *
 * `public/sw.js` deliberately does NOT take over under somebody's hands: the
 * first worker for an origin activates at once, but an update installs and
 * then parks. Something has to notice it parked and offer the person the
 * moment — that is this module and `shell/UpdateBanner.vue`.
 *
 * TWO MODES, because both are legitimate (Devin: "so auto update or click a
 * button to update, both options should be available"):
 *
 *   prompt (default)  the banner asks. The person picks the moment because
 *                     they are the only party who knows whether anything is
 *                     half-typed.
 *   auto              no banner; it applies itself.
 *
 * Auto still does NOT reload out from under a half-filled form. It applies
 * when the update is found during the page's own load — nothing is typed yet
 * — and otherwise waits for the tab to go to the background. On a phone that
 * is every time the app is switched away from, so the wait is short and the
 * person comes back to a fresh build rather than losing what they were doing.
 * The mode is per DEVICE, in localStorage: it is a statement about this
 * handset in this orchard, not about the account.
 *
 * TWO KINDS OF UPDATE, and until 2026-09-15 only the first was noticed:
 *   - a new SHELL — a worker installs and parks, and `reg.waiting` says so;
 *   - new MODULES — the shell is byte-identical, no worker installs, and the
 *     only thing that changed is the import map. That is most deploys, and it
 *     could not raise the banner at all (see `pwa/moduleVersion.ts`).
 *
 * THREE TRIGGERS, and the third is the one that matters on a phone:
 *   - on load, once the registration is available;
 *   - every 30 minutes while the app is open;
 *   - on `visibilitychange` back to visible — an installed app spends most of
 *     its life in the background, and a device that was in a pocket all
 *     morning would otherwise never check.
 */
import { readonly, ref } from "vue";

/** True once a new worker has installed and is waiting to be let in. */
const updateReady = ref(false);
/**
 * True when the modules moved under a shell that did not. There is no worker
 * to let in, so applying is a plain reload — the worker now fetches the
 * resolve answer network-first, so the reload lands on the new map.
 */
const modulesMoved = ref(false);
/** True while an explicit "check for updates" is in flight. */
const checking = ref(false);

export const appUpdate = {
  ready: readonly(updateReady),
  checking: readonly(checking),
};

const CHECK_INTERVAL_MS = 30 * 60 * 1000;
const MODE_KEY = "metaship:updateMode";

export type UpdateMode = "prompt" | "auto";

/** Default is `prompt` — never take the page away from someone unasked. */
export function updateMode(): UpdateMode {
  try {
    return localStorage.getItem(MODE_KEY) === "auto" ? "auto" : "prompt";
  } catch {
    return "prompt";
  }
}

export function setUpdateMode(mode: UpdateMode): void {
  try {
    localStorage.setItem(MODE_KEY, mode);
  } catch {
    // Private mode / blocked storage. The app still updates, it just asks.
  }
  // Turning it on with one already waiting should not need another 30 minutes.
  if (mode === "auto" && updateReady.value) applyWhenSafe();
}

/**
 * Apply now if nothing can be lost, else at the next safe moment.
 *
 * "Safe" is: the page is hidden. `duringLoad` is the other safe moment — the
 * update was already waiting when the app started, so there is nothing on
 * screen the person has touched.
 */
function applyWhenSafe(duringLoad = false): void {
  if (duringLoad || document.visibilityState === "hidden") {
    applyUpdate();
    return;
  }
  const onHide = (): void => {
    if (document.visibilityState !== "hidden") return;
    document.removeEventListener("visibilitychange", onHide);
    applyUpdate();
  };
  document.addEventListener("visibilitychange", onHide);
}

let registration: ServiceWorkerRegistration | null = null;
/** Set by `watchForUpdates` when the shell knows how to reach the resolve answer. */
let checkModules: (() => Promise<boolean>) | null = null;
/** Guards against the double reload two `controllerchange` events would cause. */
let reloading = false;

/**
 * A worker is "waiting" when it has installed while another one controls the
 * page. Without a controller there is nothing to replace — that is a first
 * install, which activates itself, and must never raise the banner.
 */
function noteWaiting(reg: ServiceWorkerRegistration, duringLoad = false): void {
  if (!reg.waiting || !navigator.serviceWorker.controller) return;
  updateReady.value = true;
  if (updateMode() === "auto") applyWhenSafe(duringLoad);
}

/**
 * Ask whether the modules moved, and raise the banner if they did.
 *
 * Never LOWERS it: a check that fails, or that races a deploy mid-flight, must
 * not take away an update the person was already offered.
 */
async function noteModules(): Promise<void> {
  if (!checkModules || updateReady.value) return;
  if (await checkModules()) {
    modulesMoved.value = true;
    updateReady.value = true;
    // Auto mode covers module-only deploys too (the commonest kind), at the
    // same safe moment as a shell update — never mid-task.
    if (updateMode() === "auto") applyWhenSafe();
  }
}

/**
 * Begin watching a registration for updates. Called once, by
 * `registerServiceWorker`, with the registration it just made.
 */
export function watchForUpdates(
  reg: ServiceWorkerRegistration,
  moduleCheck?: () => Promise<boolean>,
): void {
  registration = reg;
  checkModules = moduleCheck ?? null;
  // Found at startup: nothing is typed, so auto mode may apply straight away.
  noteWaiting(reg, true);
  void noteModules();

  reg.addEventListener("updatefound", () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      if (installing.state === "installed") noteWaiting(reg);
    });
  });

  // The new worker took over (ours, via applyUpdate — or another tab's).
  // Reload so the page is running the same build the worker is serving.
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) return;
    reloading = true;
    window.location.reload();
  });

  void reg.update().catch(() => undefined);
  window.setInterval(() => {
    void reg.update().catch(() => undefined);
    void noteModules();
  }, CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      void reg.update().then(() => noteWaiting(reg)).catch(() => undefined);
      // The trigger that matters on a phone: an installed app spends its life
      // in the background, and a module deploy lands while it is in a pocket.
      void noteModules();
    }
  });
}

/**
 * Let the waiting worker in. It calls `skipWaiting()`, takes control, and the
 * `controllerchange` listener above reloads the page.
 *
 * The reload is NOT scheduled here as a fallback. A timer that reloads anyway
 * would fire on the one path where nothing is waiting, throwing away whatever
 * the person was doing to achieve nothing.
 *
 * A MODULE-only update has no waiting worker to let in — the shell did not
 * change. Applying it is a plain reload, which is enough because the worker
 * fetches the resolve answer network-first: the reload resolves the new map
 * and pulls the new bundles. Without this branch the banner would appear for
 * the commonest kind of deploy and then do nothing when tapped.
 */
export function applyUpdate(): void {
  const waiting = registration?.waiting;
  if (!waiting) {
    if (modulesMoved.value) {
      reloading = true;
      window.location.reload();
      return;
    }
    updateReady.value = false;
    return;
  }
  waiting.postMessage({ type: "SKIP_WAITING" });
}

/** Dismiss the banner without updating. The next trigger raises it again. */
export function dismissUpdate(): void {
  updateReady.value = false;
}

/**
 * An explicit check, from the App info panel. Resolves to whether an update is
 * now waiting, so the caller can say "you are up to date" rather than leaving
 * a button that appears to do nothing.
 */
export async function checkForUpdate(): Promise<boolean> {
  if (!registration) return false;
  checking.value = true;
  try {
    await registration.update();
    noteWaiting(registration);
    await noteModules();
    return updateReady.value;
  } catch {
    return false;
  } finally {
    checking.value = false;
  }
}

/**
 * The last resort, for a device that is wedged on a bad build: drop every
 * cache, unregister every worker, reload from the network.
 *
 * This is the button that gets a phone in a packhouse working again over a
 * phone call. It does NOT touch localStorage or IndexedDB — the session and
 * any queued offline work survive, which is the difference between this and
 * signing out.
 */
export async function forceReinstall(): Promise<void> {
  try {
    if ("caches" in window) {
      const names = await caches.keys();
      await Promise.all(names.map((name) => caches.delete(name)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((reg) => reg.unregister()));
    }
  } catch {
    // Reload regardless — a partial clear still beats the wedged build.
  }
  reloading = true;
  window.location.reload();
}

/** Test seam: drop the module-level registration between cases. */
export function resetForTests(): void {
  registration = null;
  checkModules = null;
  reloading = false;
  updateReady.value = false;
  modulesMoved.value = false;
  checking.value = false;
}
